import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Tag, Spin } from "antd";
import { Link } from "react-router-dom";
import { ShoppingCartOutlined, ShopOutlined, InboxOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";

const formatCurrency = (n) =>
	`₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ProductHistoryModal = ({ productId, productName, visible, onClose }) => {
	const [loading, setLoading] = useState(false);
	const [history, setHistory] = useState({ purchaseHistory: [], saleHistory: [], openingStock: [] });

	useEffect(() => {
		if (!visible || !productId) {
			setHistory({ purchaseHistory: [], saleHistory: [], openingStock: [] });
			return;
		}
		setLoading(true);
		axios
			.get(`product/history?product_id=${productId}`)
			.then((res) => {
				setHistory({
					purchaseHistory: res.data.purchaseHistory || [],
					saleHistory: res.data.saleHistory || [],
					openingStock: res.data.openingStock || [],
				});
			})
			.catch(() => setHistory({ purchaseHistory: [], saleHistory: [], openingStock: [] }))
			.finally(() => setLoading(false));
	}, [visible, productId]);

	const mergedRows = useMemo(() => {
		const purchase = (history.purchaseHistory || []).map((r, i) => ({
			...r,
			key: `p-${r.date}-${r.invoiceId}-${i}`,
			dateSort: new Date(r.date),
		}));
		const sale = (history.saleHistory || []).map((r, i) => ({
			...r,
			key: `s-${r.date}-${r.invoiceId}-${i}`,
			dateSort: new Date(r.date),
		}));
		const opening = (history.openingStock || []).map((r, i) => ({
			...r,
			key: `o-${r.date}-${i}`,
			dateSort: new Date(r.date),
		}));
		return [...purchase, ...sale, ...opening].sort((a, b) => b.dateSort - a.dateSort);
	}, [history.purchaseHistory, history.saleHistory, history.openingStock]);

	const quantitySummary = useMemo(() => {
		const opening = (history.openingStock || []).reduce((s, r) => s + (Number(r.quantity) || 0), 0);
		const purchased = (history.purchaseHistory || []).reduce((s, r) => s + (Number(r.quantity) || 0), 0);
		const sold = (history.saleHistory || []).reduce((s, r) => s + (Number(r.quantity) || 0), 0);
		return { opening, purchased, sold, net: opening + purchased - sold };
	}, [history.openingStock, history.purchaseHistory, history.saleHistory]);

	const columns = [
		{
			title: "Type",
			dataIndex: "type",
			key: "type",
			width: 110,
			render: (type) => {
				if (type === "Purchase") return <Tag icon={<ShoppingCartOutlined />} color="blue">Purchase</Tag>;
				if (type === "Opening Stock") return <Tag icon={<InboxOutlined />} color="orange">Opening Stock</Tag>;
				return <Tag icon={<ShopOutlined />} color="green">Sale</Tag>;
			},
		},
		{
			title: "Date",
			dataIndex: "date",
			key: "date",
			width: 120,
			render: (date) => moment(date).format("DD MMM YYYY"),
		},
		{
			title: "Party",
			dataIndex: "party",
			key: "party",
			ellipsis: true,
		},
		{
			title: "Qty",
			dataIndex: "quantity",
			key: "quantity",
			width: 80,
			align: "right",
		},
		{
			title: "Unit Price",
			dataIndex: "unitPrice",
			key: "unitPrice",
			width: 110,
			align: "right",
			render: (v, row) => row.type === "Opening Stock" ? "—" : formatCurrency(v),
		},
		{
			title: "Total",
			dataIndex: "total",
			key: "total",
			width: 120,
			align: "right",
			render: (v, row) => row.type === "Opening Stock" ? "—" : formatCurrency(v),
		},
		{
			title: "Discount",
			dataIndex: "discount",
			key: "discount",
			width: 100,
			align: "right",
			render: (v, row) => row.type === "Opening Stock" ? "—" : formatCurrency(v),
		},
		{
			title: "Invoice",
			key: "invoiceRef",
			width: 140,
			render: (_, row) => {
				if (row.type === "Opening Stock" || !row.invoiceId || row.invoiceRef === "—") return row.invoiceRef ?? "—";
				return row.type === "Sale" ? (
					<Link to={`/sale/${row.invoiceId}`}>{row.invoiceRef}</Link>
				) : (
					<Link to={`/purchase/${row.invoiceId}`}>{row.invoiceRef}</Link>
				);
			},
		},
	];

	return (
		<Modal
			title={productName ? `Product History — ${productName}` : "Product History"}
			open={visible}
			onCancel={onClose}
			footer={null}
			width={800}
			destroyOnClose
		>
			{loading ? (
				<div className="text-center py-4">
					<Spin size="large" />
				</div>
			) : (
				<>
					{mergedRows.length > 0 && (
						<div style={{ marginBottom: 12, padding: "8px 12px", background: "#fafafa", borderRadius: 6, fontSize: 13 }}>
							<strong>Closing stock:</strong> {quantitySummary.net}
						</div>
					)}
					<Table
						columns={columns}
						dataSource={mergedRows}
						pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `Total ${t} records` }}
						scroll={{ x: 700 }}
						size="small"
						locale={{ emptyText: "No purchase or sale history for this product." }}
					/>
				</>
			)}
		</Modal>
	);
};

export default ProductHistoryModal;
